import { useState } from "react";
import { studioAPI, tryParseJSON, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Youtube, ArrowRight, Loader2, Copy, Download, Info, ExternalLink, Wand2 } from "lucide-react";

export default function SourceImport() {
    const { lang, activeProjectId } = useApp();
    const [url, setUrl] = useState("");
    const [video, setVideo] = useState(null);
    const [transcript, setTranscript] = useState("");
    const [transcriptHint, setTranscriptHint] = useState("");
    const [angle, setAngle] = useState("");
    const [fetching, setFetching] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [raw, setRaw] = useState("");
    const [episodeId, setEpisodeId] = useState(null);
    const parsed = tryParseJSON(raw);

    const fetchSource = async () => {
        if (!/(youtu\.be\/|youtube\.com\/)/.test(url)) {
            toast.error(lang === "fr" ? "URL YouTube invalide" : "Invalid YouTube URL");
            return;
        }
        setFetching(true); setVideo(null); setTranscript(""); setTranscriptHint(""); setRaw(""); setEpisodeId(null);
        try {
            const d = await studioAPI.sourceFetch(url);
            setVideo(d.video);
            if (d.transcript_status === "ok") {
                setTranscript(d.transcript);
                toast.success(lang === "fr" ? "Transcription récupérée ✨" : "Transcript fetched ✨");
            } else {
                setTranscriptHint(d.transcript_hint || "");
                toast.info(lang === "fr" ? "Colle la transcription manuellement" : "Paste transcript manually");
            }
        } catch (e) { toast.error(e?.response?.data?.detail || "Fetch error"); }
        finally { setFetching(false); }
    };

    const generate = async () => {
        if (!transcript || transcript.trim().length < 40) {
            toast.error(lang === "fr" ? "Transcription trop courte (min 40 caractères)" : "Transcript too short (min 40 chars)");
            return;
        }
        setGenerating(true); setRaw(""); setEpisodeId(null);
        try {
            const d = await studioAPI.sourceShort({
                video_id: video?.video_id,
                video_title: video?.title || "",
                transcript,
                angle,
                language: lang,
                project_id: activeProjectId,
            });
            setRaw(d.result);
            setEpisodeId(d.id);
            window.dispatchEvent(new Event("sf:quota-changed"));
            toast.success(lang === "fr" ? "Short généré ✨" : "Short generated ✨");
        } catch (e) { toast.error(e?.response?.data?.detail || "Generation error"); }
        finally { setGenerating(false); }
    };

    const downloadBundle = async () => {
        if (!episodeId) return;
        const token = localStorage.getItem("sf_token");
        const r = await fetch(`${API}/export/episode/${episodeId}`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await r.blob();
        const link = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = link; a.download = `shorts-factory-${episodeId.slice(0, 8)}.txt`;
        a.click(); URL.revokeObjectURL(link);
    };

    return (
        <div data-testid="source-import-container" className="grid lg:grid-cols-5 gap-6">
            {/* LEFT — inputs */}
            <div className="lg:col-span-2 space-y-4">
                <div>
                    <div className="overline">12 · Import Source</div>
                    <h2 className="font-display text-2xl font-black uppercase mt-1">
                        {lang === "fr" ? "Importe ta source" : "Import your source"}
                    </h2>
                    <p className="text-sm text-slate-400 mt-2">
                        {lang === "fr"
                            ? "Colle une URL YouTube longue → l'IA en extrait l'insight le plus fort et le distille en un Short 45-60s prêt à publier."
                            : "Paste a long YouTube URL → the AI extracts the strongest insight and distills it into a 45-60s Short ready to publish."}
                    </p>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2">
                        <Youtube className="w-3.5 h-3.5 text-[#FF0033]" /> {lang === "fr" ? "URL YouTube" : "YouTube URL"}
                    </label>
                    <div className="mt-2 flex gap-2">
                        <input
                            data-testid="source-url-input"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=..."
                            className="flex-1 bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]"
                        />
                        <button
                            data-testid="source-fetch-btn"
                            onClick={fetchSource}
                            disabled={fetching || !url}
                            className="px-4 py-2.5 rounded border border-[#253046] hover:border-[#FF4D00] text-sm text-slate-100 flex items-center gap-1.5 kinetic disabled:opacity-60"
                        >
                            {fetching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                            {lang === "fr" ? "Fetch" : "Fetch"}
                        </button>
                    </div>
                </div>

                {video && (
                    <div data-testid="source-video-preview" className="bg-[#161D2A] border border-[#253046] rounded p-3 flex gap-3">
                        <img src={video.thumbnail} alt="" className="w-24 aspect-video object-cover rounded" />
                        <div className="flex-1 min-w-0">
                            <div className="font-display uppercase text-sm font-bold text-white truncate">{video.title || "—"}</div>
                            <div className="text-xs text-slate-400 truncate">{video.author}</div>
                            <a href={`https://www.youtube.com/watch?v=${video.video_id}`} target="_blank" rel="noreferrer"
                                className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono-sf text-[#FF4D00] uppercase tracking-widest hover:underline">
                                <ExternalLink className="w-3 h-3" /> {video.video_id}
                            </a>
                        </div>
                    </div>
                )}

                {transcriptHint && (
                    <div className="rounded border border-[#FFB800]/30 bg-[#FFB800]/5 p-3 text-xs text-[#FFB800] flex gap-2">
                        <Info className="w-4 h-4 shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{transcriptHint}</span>
                    </div>
                )}

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">
                        {lang === "fr" ? "Transcription" : "Transcript"} <span className="text-slate-600">({transcript.length} {lang === "fr" ? "car." : "chars"})</span>
                    </label>
                    <textarea
                        data-testid="source-transcript-input"
                        value={transcript}
                        onChange={(e) => setTranscript(e.target.value)}
                        rows={7}
                        placeholder={lang === "fr" ? "Colle ici la transcription YouTube (bouton 'Afficher la transcription')..." : "Paste the YouTube transcript here ('Show transcript' button)..."}
                        className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00] resize-none font-mono-sf"
                    />
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">
                        {lang === "fr" ? "Angle (optionnel)" : "Angle (optional)"}
                    </label>
                    <input
                        data-testid="source-angle-input"
                        value={angle}
                        onChange={(e) => setAngle(e.target.value)}
                        placeholder={lang === "fr" ? "Ex: 1 conseil actionnable pour la semaine" : "e.g. 1 actionable tip for this week"}
                        className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]"
                    />
                </div>

                <button
                    data-testid="source-generate-btn"
                    onClick={generate}
                    disabled={generating || !transcript}
                    className="w-full flame-gradient text-black font-semibold py-3 rounded flex items-center justify-center gap-2 kinetic flame-glow disabled:opacity-60"
                >
                    {generating ? "···" : <>{lang === "fr" ? "Distiller en Short" : "Distill into Short"} <ArrowRight className="w-4 h-4" /></>}
                </button>
            </div>

            {/* RIGHT — output */}
            <div data-testid="source-result-panel" className="lg:col-span-3 rounded border border-[#253046] bg-[#0B111B] p-5 min-h-[500px]">
                {!raw && !generating && (
                    <div className="h-full flex flex-col items-center justify-center text-center py-20">
                        <Wand2 className="w-10 h-10 text-slate-700 mb-3" />
                        <p className="text-sm text-slate-500">{lang === "fr" ? "Le Short distillé apparaîtra ici." : "The distilled Short will appear here."}</p>
                    </div>
                )}
                {generating && <div className="h-full flex items-center justify-center text-slate-400 font-mono-sf text-xs">DISTILLING INSIGHT ···</div>}

                {parsed && (
                    <div className="space-y-5">
                        <div className="flex justify-between items-start gap-3">
                            <div>
                                <div className="overline">Title</div>
                                <div className="font-display text-2xl font-black uppercase mt-1">{parsed.title}</div>
                                {parsed.source_credit && (
                                    <div className="mt-1 text-xs text-slate-500 italic">{parsed.source_credit}</div>
                                )}
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button onClick={() => { navigator.clipboard.writeText(raw); toast.success("Copié"); }} className="text-xs text-slate-400 hover:text-white flex items-center gap-1 border border-[#253046] px-2 py-1 rounded"><Copy className="w-3 h-3" /> All</button>
                                {episodeId && (
                                    <button
                                        data-testid="source-export-btn"
                                        onClick={downloadBundle}
                                        className="text-xs font-semibold text-black bg-[#FF4D00] hover:brightness-110 flex items-center gap-1 px-2.5 py-1 rounded kinetic"
                                    >
                                        <Download className="w-3 h-3" /> Bundle
                                    </button>
                                )}
                            </div>
                        </div>

                        {parsed.angle_summary && (
                            <div className="bg-gradient-to-r from-[#FFB800]/15 to-transparent border-l-2 border-[#FFB800] rounded-r p-3">
                                <div className="overline">Insight extrait</div>
                                <p className="text-sm text-slate-100 mt-1 italic">{parsed.angle_summary}</p>
                            </div>
                        )}

                        {parsed.hook && (
                            <div>
                                <div className="overline">Hooks (3 options)</div>
                                <div className="mt-2 space-y-1.5">
                                    {(Array.isArray(parsed.hook) ? parsed.hook : [parsed.hook]).map((h, i) => (
                                        <div key={i} className="bg-[#161D2A] border-l-2 border-[#FF4D00] rounded-r p-3 text-sm">{h}</div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {parsed.script && (
                            <div>
                                <div className="overline">Script</div>
                                <pre className="mt-2 bg-[#161D2A] border border-[#253046] rounded p-3 text-sm text-slate-200 whitespace-pre-wrap font-sans">
                                    {typeof parsed.script === "string" ? parsed.script : JSON.stringify(parsed.script, null, 2)}
                                </pre>
                            </div>
                        )}

                        {parsed.storyboard && (
                            <div>
                                <div className="overline">Storyboard · {parsed.storyboard.length} shots</div>
                                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                                    {parsed.storyboard.map((s, i) => (
                                        <div key={i} className="bg-[#161D2A] border border-[#253046] rounded p-3">
                                            <div className="font-mono-sf text-[10px] text-[#FF4D00]">SHOT {String(i + 1).padStart(2, "0")}</div>
                                            <div className="text-xs text-slate-300 mt-1">{s.visual_prompt}</div>
                                            {s.on_screen_text && <div className="text-xs text-[#FFB800] mt-2 font-medium">« {s.on_screen_text} »</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {parsed.hashtags && (
                            <div>
                                <div className="overline">Hashtags</div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {parsed.hashtags.map((h, i) => <span key={i} className="text-xs px-2 py-1 rounded bg-[#0099FF]/10 border border-[#0099FF]/30 text-[#0099FF]">#{h.replace(/^#/, "")}</span>)}
                                </div>
                            </div>
                        )}
                        {parsed.cta && (
                            <div className="bg-gradient-to-r from-[#FF4D00]/20 to-transparent border-l-2 border-[#FF4D00] p-3 rounded-r">
                                <div className="overline">CTA</div>
                                <div className="text-sm mt-1 text-white font-medium">{parsed.cta}</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
